import React from 'react';
import { Card, Dropdown } from '@bynder/design-system';
import { IconAdd, IconDelete, IconEdit, IconMore, IconRefresh, IconStateIndicatorM } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { PartialComponent } from 'packages/services/ComponentService';
import { useFriendlyTime } from '~/hooks/useFriendlyTime';
import { StyledInlineBanner, StyledSectionItem, StyledSectionTitle } from './styled';

interface Props {
    component: PartialComponent;
    collectionName: string;
    onApply: (isUniqueContentProperties: boolean) => void;
    onEdit: () => void;
    onDelete: () => void;
    onRefreshThumbnail?: () => void;
    isDisabled?: boolean;
}

const ComponentMenu = ({
    component,
    collectionName,
    onApply,
    onEdit,
    onDelete,
    onRefreshThumbnail,
    isDisabled = false,
}: Props) => {
    const { translate } = useTranslate();
    const { getFriendlyTime } = useFriendlyTime();

    return (
        <Dropdown
            noMaxHeight
            trigger={({ isOpen, ...triggerProps }) => (
                <Card.ContextActionButton
                    aria-label={translate('components.overview.card.menu')}
                    isPressed={isOpen}
                    {...triggerProps}
                >
                    <IconMore />
                </Card.ContextActionButton>
            )}
        >
            {isDisabled && (
                <StyledInlineBanner>{translate('components.overview.card.menu.videoIncompatible')}</StyledInlineBanner>
            )}
            <StyledSectionTitle>{component.name}</StyledSectionTitle>
            {component.description && <StyledSectionItem>{component.description}</StyledSectionItem>}
            <StyledSectionItem>
                <IconStateIndicatorM size="24" />
                {collectionName}
            </StyledSectionItem>
            <StyledSectionItem>
                <IconStateIndicatorM size="24" />
                {translate('components.overview.card.menu.created', { date: getFriendlyTime(component.created) })}
            </StyledSectionItem>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => onApply(true)} icon={<IconAdd />} isDisabled={isDisabled}>
                {translate('components.overview.card.menu.uniqueContentProperties')}
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => onEdit()} icon={<IconEdit />}>
                {translate('components.overview.card.menu.editDetails')}
            </Dropdown.Item>
            {onRefreshThumbnail && (
                <>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={onRefreshThumbnail} icon={<IconRefresh />}>
                        {translate('components.overview.card.menu.refreshThumbnail')}
                    </Dropdown.Item>
                </>
            )}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => onDelete()} icon={<IconDelete />}>
                {translate('components.overview.card.menu.delete')}
            </Dropdown.Item>
        </Dropdown>
    );
};

export default ComponentMenu;
